/**
 * useLearningImpactLoop Hooks
 * 
 * Phase 3: Learning Impact Loop
 * Hooks for spawning outcome windows and fetching effect signals.
 * 
 * Terminology: "engagement signals" not "effectiveness scores"
 * Philosophy: Assist, not judge. Slow learning. No penalties.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import type { 
  TrainingOutcomeWindow, 
  TrainingEffectSignal,
  EffectSignalType,
  EffectDirection 
} from '@/types/training';

// =============================================
// SPAWN OUTCOME WINDOW
// =============================================

interface SpawnWindowInput {
  technicianProfileId: string;
  artifactId: string;
  domainKey: string;
  engagementEventId?: string;
}

/**
 * Spawn observation windows after training engagement completion
 * Creates 7d, 14d, and 30d windows with baseline metrics snapshot
 */
export function useSpawnOutcomeWindow() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: SpawnWindowInput) => {
      const { data, error } = await supabase.functions.invoke('spawn-outcome-window', {
        body: input,
      });

      if (error) throw error;
      return data;
    },
    onSuccess: (_, variables) => {
      // Invalidate outcome windows query
      queryClient.invalidateQueries({
        queryKey: ['outcome-windows', variables.technicianProfileId],
      });
    },
  });
}

// =============================================
// FETCH OUTCOME WINDOWS
// =============================================

/**
 * Fetch outcome windows for a technician
 */
export function useOutcomeWindows(
  technicianProfileId: string | undefined,
  options?: { status?: 'active' | 'completed' | 'insufficient_data'; limit?: number }
) {
  return useQuery({
    queryKey: ['outcome-windows', technicianProfileId, options],
    queryFn: async () => {
      if (!technicianProfileId) return [];

      let query = supabase
        .from('training_outcome_windows')
        .select('*')
        .eq('technician_profile_id', technicianProfileId)
        .order('created_at', { ascending: false });

      if (options?.status) {
        query = query.eq('status', options.status);
      }

      if (options?.limit) {
        query = query.limit(options.limit);
      }

      const { data, error } = await query;
      if (error) throw error;

      return (data || []) as TrainingOutcomeWindow[];
    },
    enabled: !!technicianProfileId,
  });
}

// =============================================
// FETCH EFFECT SIGNALS
// =============================================

/**
 * Fetch effect signals for an artifact (aggregate view)
 * Used in admin dashboard and recommendation engine
 */
export function useArtifactEffectSignals(artifactId: string | undefined) {
  return useQuery({
    queryKey: ['effect-signals', 'artifact', artifactId],
    queryFn: async () => {
      if (!artifactId) return [];

      const { data, error } = await supabase
        .from('training_effect_signals')
        .select('*')
        .eq('artifact_id', artifactId);

      if (error) throw error;

      return (data || []) as TrainingEffectSignal[];
    },
    enabled: !!artifactId,
  });
}

/**
 * Fetch effect signals for a domain (for recommendation weighting)
 */
export function useDomainEffectSignals(domainKey: string | undefined) {
  return useQuery({
    queryKey: ['effect-signals', 'domain', domainKey],
    queryFn: async () => {
      if (!domainKey) return [];

      const { data, error } = await supabase
        .from('training_effect_signals')
        .select('*')
        .eq('domain_key', domainKey)
        .gt('sample_size', 0);

      if (error) throw error;

      return (data || []) as TrainingEffectSignal[];
    },
    enabled: !!domainKey,
  });
}

/**
 * Fetch all effect signals with positive direction
 * Used for "This training has helped others like you" indicators
 */
export function usePositiveEffectSignals(artifactIds?: string[]) {
  return useQuery({
    queryKey: ['effect-signals', 'positive', artifactIds],
    queryFn: async () => {
      let query = supabase
        .from('training_effect_signals')
        .select('*')
        .eq('direction', 'positive')
        .gte('sample_size', 5); // Only show if we have enough samples

      if (artifactIds && artifactIds.length > 0) {
        query = query.in('artifact_id', artifactIds);
      }

      const { data, error } = await query;
      if (error) throw error;

      return (data || []) as TrainingEffectSignal[];
    },
    enabled: true,
  });
}

// =============================================
// AGGREGATE EFFECT SIGNALS FOR ADMIN
// =============================================

interface AggregateEffectSummary {
  artifact_id: string;
  artifact_title?: string;
  total_samples: number;
  positive_signals: number;
  neutral_signals: number;
  negative_signals: number;
  avg_confidence: number;
  dominant_direction: EffectDirection;
  signal_types: EffectSignalType[];
}

/**
 * Fetch aggregated effect signals for admin dashboard
 * Shows artifact-level impact, NOT technician-level
 */
export function useAggregateEffectSignals(options?: { 
  domainKey?: string; 
  minSamples?: number;
  limit?: number;
}) {
  return useQuery({
    queryKey: ['effect-signals', 'aggregate', options],
    queryFn: async () => {
      let query = supabase
        .from('training_effect_signals')
        .select(`
          artifact_id,
          domain_key,
          signal_type,
          direction,
          confidence_weight,
          sample_size,
          positive_samples,
          negative_samples
        `)
        .gte('sample_size', options?.minSamples || 1);

      if (options?.domainKey) {
        query = query.eq('domain_key', options.domainKey);
      }

      const { data: signals, error } = await query;
      if (error) throw error;

      // Group by artifact_id
      const artifactMap = new Map<string, TrainingEffectSignal[]>();
      for (const signal of signals || []) {
        const existing = artifactMap.get(signal.artifact_id) || [];
        existing.push(signal as TrainingEffectSignal);
        artifactMap.set(signal.artifact_id, existing);
      }

      // Build aggregate summaries
      const summaries: AggregateEffectSummary[] = [];
      for (const [artifactId, artifactSignals] of artifactMap) {
        const totalSamples = artifactSignals.reduce((sum, s) => sum + s.sample_size, 0);
        const positiveSignals = artifactSignals.filter(s => s.direction === 'positive').length;
        const neutralSignals = artifactSignals.filter(s => s.direction === 'neutral').length;
        const negativeSignals = artifactSignals.filter(s => s.direction === 'negative').length;
        const avgConfidence = artifactSignals.reduce((sum, s) => sum + s.confidence_weight, 0) / artifactSignals.length;

        let dominantDirection: EffectDirection = 'neutral';
        if (positiveSignals > negativeSignals && positiveSignals > neutralSignals) {
          dominantDirection = 'positive';
        } else if (negativeSignals > positiveSignals && negativeSignals > neutralSignals) {
          dominantDirection = 'negative';
        }

        summaries.push({
          artifact_id: artifactId,
          total_samples: totalSamples,
          positive_signals: positiveSignals,
          neutral_signals: neutralSignals,
          negative_signals: negativeSignals,
          avg_confidence: avgConfidence,
          dominant_direction: dominantDirection,
          signal_types: artifactSignals.map(s => s.signal_type as EffectSignalType),
        });
      }

      // Sort by total samples descending
      summaries.sort((a, b) => b.total_samples - a.total_samples);

      if (options?.limit) {
        return summaries.slice(0, options.limit);
      }

      return summaries;
    },
  });
}

// =============================================
// HELPER: GET EFFECT WEIGHT FOR RECOMMENDATIONS
// =============================================

/**
 * Calculate training effect weight for recommendation scoring
 * Returns a value between -0.3 and +0.3 (capped influence)
 */
export async function getTrainingEffectWeight(
  artifactId: string,
  targetDomains: string[]
): Promise<number> {
  const MAX_INFLUENCE = 0.30;

  try {
    const { data: signals, error } = await supabase
      .from('training_effect_signals')
      .select('confidence_weight, sample_size')
      .eq('artifact_id', artifactId)
      .in('domain_key', targetDomains)
      .gte('sample_size', 5); // Only use signals with sufficient samples

    if (error || !signals || signals.length === 0) {
      return 0;
    }

    // Calculate weighted average confidence
    const totalWeight = signals.reduce((sum, s) => sum + s.sample_size, 0);
    const weightedConfidence = signals.reduce(
      (sum, s) => sum + s.confidence_weight * s.sample_size,
      0
    ) / totalWeight;

    // Cap the influence
    return Math.max(-MAX_INFLUENCE, Math.min(MAX_INFLUENCE, weightedConfidence * MAX_INFLUENCE));
  } catch {
    return 0;
  }
}
